import type {Module} from "vuex";
import type {RootState} from "@/store/types";
import {useStorage} from "@/api/storage/local";
import {platform} from "@/api/platform";
import {retryRequest} from "@/helpers/request";
import {PERMISSIONS} from "@/constants";

const STORAGE_COUNTRY_KEY = "user_country";

export const INITIAL_PROFILE_VERSION = "1.0";
export const INITIAL_EQUIPMENT_VERSION = "1.0";

// Quiz version per country, with a "default" fallback (GET /api/v2/profileversion, /api/v2/equipmentversion)
export interface QuizVersions {
    default: string
    [country: string]: string
}

// Style profile as returned by GET/POST /api/v2/widget/style-profile
export type StyleProfile = Partial<ProfileState> & Record<string, unknown>

export interface ProfileState {
    loaded: boolean | null
    name: string | null
    email: string | null
    language: string | null
    country: string | null
    workplace: unknown
    priority: unknown
    priorityDetails: unknown
    secondaryPriority: unknown
    secondaryPriorityDetails: unknown
    improveWorkspace: unknown
    improveRemoteWorking: unknown

    // Headset
    receivedEquipment: unknown
    issues: unknown
    issuesDescription: unknown
    // Keyboard and mouse
    receivedKeyboardMouse: unknown
    issuesKeyboardMouse: unknown
    issuesDescriptionKeyboardMouse: unknown
    // Webcam
    receivedWebcam: unknown
    issuesWebcam: unknown
    issuesDescriptionWebcam: unknown
    // Monitor
    receivedMonitor: unknown
    issuesMonitor: unknown
    issuesDescriptionMonitor: unknown
    // Usbhub
    receivedUsbhub: unknown
    issuesUsbhub: unknown
    issuesDescriptionUsbhub: unknown

    profileVersions: QuizVersions | null
    profile_submitted: boolean | null
    allowed_profile_version: string | null
    profile_submitted_version: string | null

    equipmentVersions: QuizVersions | null
    equipment_submitted: boolean | null
    allowed_equipment_version: string | null
    equipment_submitted_version: string | null
}

const profile: Module<ProfileState, RootState> = {
    namespaced: true,
    state: {
        loaded: false,
        name: null,
        email: null,
        language: null,
        country: null,
        workplace: null,
        priority: null,
        priorityDetails: null,
        secondaryPriority: null,
        secondaryPriorityDetails: null,
        improveWorkspace: null,
        improveRemoteWorking: null,

        // Headset
        receivedEquipment: null,
        issues: null,
        issuesDescription: null,
        // Keyboard and mouse
        receivedKeyboardMouse: null,
        issuesKeyboardMouse: null,
        issuesDescriptionKeyboardMouse: null,
        // Webcam
        receivedWebcam: null,
        issuesWebcam: null,
        issuesDescriptionWebcam: null,
        // Monitor
        receivedMonitor: null,
        issuesMonitor: null,
        issuesDescriptionMonitor: null,
        // Usbhub
        receivedUsbhub: null,
        issuesUsbhub: null,
        issuesDescriptionUsbhub: null,

        profileVersions: null,
        profile_submitted: null,
        allowed_profile_version: null,
        profile_submitted_version: null,

        equipmentVersions: null,
        equipment_submitted: null,
        allowed_equipment_version: null,
        equipment_submitted_version: null,
    },
    getters: {
        loaded: state => state.loaded,
        name: state => state.name,
        firstName: (state): string | null => null !== state.name ? state.name.split(" ")[0] : null,
        email: state => state.email,
        language: state => state.language,
        country: (state): string | null => state.country ? state.country : useStorage()!.get(STORAGE_COUNTRY_KEY, null),
        workplace: state => state.workplace,
        priority: state => state.priority,
        priorityDetails: state => state.priorityDetails,
        secondaryPriority: state => state.secondaryPriority,
        secondaryPriorityDetails: state => state.secondaryPriorityDetails,
        improveWorkspace: state => state.improveWorkspace,
        improveRemoteWorking: state => state.improveRemoteWorking,
        profileVersions: state => state.profileVersions,
        equipmentVersions: state => state.equipmentVersions,

        // Headset
        receivedEquipment: state => state.receivedEquipment,
        issues: state => state.issues,
        issuesDescription: state => state.issuesDescription,
        // Keyboard and mouse
        receivedKeyboardMouse: state => state.receivedKeyboardMouse,
        issuesKeyboardMouse: state => state.issuesKeyboardMouse,
        issuesDescriptionKeyboardMouse: state => state.issuesDescriptionKeyboardMouse,
        // Webcam
        receivedWebcam: state => state.receivedWebcam,
        issuesWebcam: state => state.issuesWebcam,
        issuesDescriptionWebcam: state => state.issuesDescriptionWebcam,
        // Monitor
        receivedMonitor: state => state.receivedMonitor,
        issuesMonitor: state => state.issuesMonitor,
        issuesDescriptionMonitor: state => state.issuesDescriptionMonitor,
        // Usbhub
        receivedUsbhub: state => state.receivedUsbhub,
        issuesUsbhub: state => state.issuesUsbhub,
        issuesDescriptionUsbhub: state => state.issuesDescriptionUsbhub,

        // Profile versioning
        profile_submitted: state => state.profile_submitted,
        equipment_submitted: (state): boolean => !!(state.equipment_submitted && state.country),
        profile_submitted_version: (state): string | null => {

            if (!state.profile_submitted_version) {

                return state.profile_submitted ? INITIAL_PROFILE_VERSION : null;
            }

            return state.profile_submitted_version;
        },
        equipment_submitted_version: (state): string | null => {

            if (!state.equipment_submitted_version) {

                return state.equipment_submitted ? INITIAL_EQUIPMENT_VERSION : null;
            }

            return state.equipment_submitted_version;
        },
        allowed_profile_version: (state): string => {

            if (state.allowed_profile_version) {

                return state.allowed_profile_version;
            }

            return INITIAL_PROFILE_VERSION;
        },
        allowed_equipment_version: (state): string => {

            if (state.allowed_equipment_version) {

                return state.allowed_equipment_version;
            }

            return INITIAL_EQUIPMENT_VERSION;
        },

        // Quiz
        forceQuizProfile: (state): boolean => {

            if (1 != Number(process.env.VUE_APP_QUIZ_PROFILE_VERSIONING_ENABLED)) {

                return false;
            }

            if (!state.profile_submitted || !state.country) {

                // Profile was never submitted
                return true;
            }

            const profileVersions = state.profileVersions as QuizVersions;

            // Retrieve submitted profile version
            const profileSubmittedVersion = state.profile_submitted_version || profileVersions.default;
            const allowedProfileVersion = profileVersions[state.country]
                ? profileVersions[state.country]
                : profileVersions.default;

            return profileSubmittedVersion != allowedProfileVersion;
        },
        forceQuizEquipment: (state, getters, rootState): boolean => {

            if (-1 === (rootState.user.permissions.permissions as string[]).indexOf(PERMISSIONS.EMPLOYEE)) {

                return false;
            }

            if (!state.equipment_submitted || !state.country) {

                // Profile was never submitted
                return true;
            }

            if (1 != Number(process.env.VUE_APP_QUIZ_EQUIPMENT_VERSIONING_ENABLED)) {

                return false;
            }

            const equipmentVersions = state.equipmentVersions as QuizVersions;

            // Retrieve submitted profile version
            const equipmentSubmittedVersion = state.equipment_submitted_version || equipmentVersions.default;
            const allowedEquipmentVersion = equipmentVersions[state.country]
                ? equipmentVersions[state.country]
                : equipmentVersions.default;

            return equipmentSubmittedVersion != allowedEquipmentVersion;
        },
        profile: (state): ProfileState => JSON.parse(JSON.stringify(state))
    },
    mutations: {
        updateStorage: state => useStorage()!.set(STORAGE_COUNTRY_KEY, state.country)
    },
    actions: {
        fetch(context) {

            const promises = [
                retryRequest(() => platform.get("/api/v2/widget/style-profile"), undefined, undefined, "get:/api/v2/widget/style-profile")
            ];

            if (1 == Number(process.env.VUE_APP_QUIZ_PROFILE_VERSIONING_ENABLED)) {
                promises.push(
                    retryRequest(() => platform.get("/api/v2/profileversion"), undefined, undefined, "get:/api/v2/profileversion")
                )
            }

            if (1 == Number(process.env.VUE_APP_QUIZ_EQUIPMENT_VERSIONING_ENABLED)) {
                promises.push(
                    retryRequest(() => platform.get("/api/v2/equipmentversion"), undefined, undefined, "get:/api/v2/equipmentversion")
                )
            }

            return Promise.all(promises).then(responses => {

                context.state.loaded = true;

                const [profileResponse, profileVersionsResponse, equipmentVersionsResponse] = responses;

                const profile: StyleProfile = profileResponse.data.results;

                if (profileVersionsResponse) {
                    context.state.profileVersions = profileVersionsResponse.data;
                }

                if (equipmentVersionsResponse) {
                    context.state.equipmentVersions = equipmentVersionsResponse.data;
                }

                Object.keys(profile).forEach(key => (context.state as unknown as Record<string, unknown>)[key] = profile[key]);

                // Set boolean to understand profile already loaded
                context.state.profile_submitted = !!profile.profile_submitted;
                context.state.equipment_submitted = !!profile.equipment_submitted;

                context.commit("updateStorage");

                if (this.getters["user/permissions/hasPermissionAccess"](PERMISSIONS.EMPLOYEE)) {

                    if (!!(profile.country && profile.equipment_submitted)) {

                        return this.dispatch("user/address/fetch");
                    }
                }
            });
        },
        post(context, payload: StyleProfile) {

            return retryRequest(
                () =>
                    platform.post<{results: StyleProfile}>("/api/v2/widget/style-profile", payload),
                [1000, 2000],
                // 403 for country means saved payload in spite of 403 response
                (error) => !(403 === error?.response?.status && undefined !== payload.country)
            )
                .then(response => {

                    const profile = response.data.results;

                    Object.keys(profile).forEach(key => (context.state as unknown as Record<string, unknown>)[key] = profile[key]);

                    context.commit("updateStorage");

                    if (!!(response.data.results.country && response.data.results.profile_submitted)) {

                        return this.dispatch("user/address/fetch");
                    }

                    return response.data.results;
                })
        },
        setCountry(context, country: string | null) {

            context.state.country = country
        },
        clear(context) {

            Object.keys(context.state).forEach(key => (context.state as unknown as Record<string, unknown>)[key] = null);

            context.commit("updateStorage");
        }
    }
}

export default profile
